import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Popper,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import SearchIcon from 'common/shared-ui/src/icons/SearchIcon';

import {
  getGantPredecessorIssues,
  getProjectsWithQuery,
  getQueryIssues,
} from '../../../../packages/common/api';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import { useDebouncedFunction } from '../../../../packages/common/utils/debounce';

// Original class: searchInput
const StyledSearchInput = styled(Autocomplete)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '10px 12px 10px 40px',
    lineHeight: '20px',
    height: 'auto',
    background: '#fff',
    '& .MuiAutocomplete-input': {
      fontSize: '14px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
    },
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&:hover': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      background: '#fff',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    maxWidth: '300px!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-loading': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));

const SearchInput = ({ intl }) => {
  const isSmallScreen = useMediaQuery(theme =>
    theme.breakpoints.maxWidth('sm'),
  );
  const [searchValue, setSearchValue] = React.useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSearching = useCallback(async value => {
    setSearchValue(value);
    try {
      setLoading(true);
      const issues = getQueryIssues({
        searchText: value,
      });
      const projects = getProjectsWithQuery(
        `filters=project.project;in;${value}`,
      );
      const response = await Promise.all([issues, projects]);
      if (response) {
        setLoading(false);
        setSearchOptions(response[0].data.concat(response[1].rows));
      }
    } catch (error) {
      console.error('ERROR WITH EDIT', error);
    }
  }, []);
  const handleSearchSelect = useCallback(
    value => {
      const issueId = value.rowId
        ? `/projects/${value.rowId}`
        : `/issues/${value.valueId}`;
      navigate(issueId);
      setSearchValue('');
      setSearchOptions([]);
    },
    [navigate],
  );
  const debouncedValueLogging = useDebouncedFunction(
    value => handleSearching(value),
    500,
  );
  const handleSearchingDebounce = e => {
    debouncedValueLogging(e);
  };
  return (
    <Box
      component={'span'}
      sx={{
        position: 'relative',
      }}
    >
      <SearchIcon
        sx={{ position: 'absolute', left: '12px', top: '8px', zIndex: 11 }}
      />
      <StyledSearchInput
        noOptionsText={intl.formatMessage({ id: 'not_found' })}
        loadingText={intl.formatMessage({ id: 'search_options' })}
        filterOptions={x => x}
        onChange={(event, newValue) => handleSearchSelect(newValue)}
        disablePortal
        options={searchOptions}
        disableClearable
        sx={{ width: '200px' }}
        onBlur={() => setSearchOptions([])}
        //value={searchValue}
        renderOption={(props, option) => {
          return (
            <MenuItem
              {...props}
              key={
                option.rowId
                  ? `${option.rowId}${option.rowName}`
                  : `${option.valueId}${option.valueName}`
              }
              value={option.rowId ? option.rowId : option.valueId}
              //selected={option.valueId === item.valueId}
            >
              {option.rowName ? option.rowName : option.valueName}
            </MenuItem>
          );
        }}
        getOptionLabel={option =>
          option.rowName
            ? option.rowName
            : option.valueName
              ? option.valueName
              : ''
        }
        isOptionEqualToValue={(option, value) => option.value === value.value}
        loading={loading}
        clearOnEscape={true}
        inputValue={searchValue}
        renderInput={params => (
          <TextField
            {...params}
            onChange={ev => handleSearching(ev.target.value)}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color='inherit' size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              },
            }}
          />
        )}
        //popupIcon={''}
        forcePopupIcon={false}
        slots={{
          popper: props => (
            <Popper {...props} placement='bottom-start'></Popper>
          ),
        }}
      />
    </Box>
  );
};

export default injectIntl(SearchInput);
